import { useEffect, useState } from 'react'
import { Spin } from 'antd'
import { useParams } from 'react-router-dom'
import { getStockReports } from '@/api/stock'
import type { StockReport } from '@/types/stockPool'
import type { StockReport as MockReport } from '../../types'

const reportTypeLabel: Record<string, string> = {
    annual: '年报', interim_h1: '中报(H1)', interim_h2: '中报(H2)',
    quarterly_q1: 'Q1', quarterly_q2: 'Q2', quarterly_q3: 'Q3', quarterly_q4: 'Q4',
    research: '研报',
}

export default function Reports({ fallbackReports }: { fallbackReports?: MockReport[] }) {
    const { code } = useParams<{ code: string }>()
    const [items, setItems] = useState<StockReport[]>([])
    const [loading, setLoading] = useState(true)
    const [expanded, setExpanded] = useState<Set<number>>(new Set())
    const [usingMock, setUsingMock] = useState(false)

    useEffect(() => {
        if (!code) return
        setLoading(true)
        getStockReports({ code: decodeURIComponent(code), report_type: '' })
            .then((res: { items?: StockReport[] }) => {
                const list = res.items ?? []
                if (list.length > 0) {
                    setItems(list)
                    setUsingMock(false)
                } else if (fallbackReports?.length) {
                    // 把 mock 研报转换为 StockReport 结构
                    setItems(fallbackReports.map((r) => ({
                        id: r.id,
                        stock_code: decodeURIComponent(code),
                        report_type: 'research',
                        title: r.title,
                        published_date: r.publish_date,
                        source_url: '',
                        foss_name: '',
                        foss_space_id: 0,
                        foss_md_name: '',
                        status: 1,
                        indicators_extract_status: 1,
                        notice_file_id: null,
                        // 保留摘要和 tags 供展示
                        _summary: r.summary,
                        _source: r.source,
                        _tags: r.tags,
                    } as any)))
                    setUsingMock(true)
                }
            })
            .catch(() => {
                if (fallbackReports?.length) {
                    setItems(fallbackReports.map(r => ({
                        id: r.id,
                        stock_code: decodeURIComponent(code),
                        report_type: 'research',
                        title: r.title,
                        published_date: r.publish_date,
                        source_url: '',
                        foss_name: '', foss_space_id: 0, foss_md_name: '',
                        status: 1, indicators_extract_status: 1, notice_file_id: null,
                        _summary: r.summary, _source: r.source, _tags: r.tags,
                    } as any)))
                    setUsingMock(true)
                }
            })
            .finally(() => setLoading(false))
    }, [code])

    const toggle = (id: number) => setExpanded(prev => {
        const next = new Set(prev)
        next.has(id) ? next.delete(id) : next.add(id)
        return next
    })

    if (loading) return <div style={{ padding: '60px 0', textAlign: 'center' }}><Spin /></div>

    if (!items.length) {
        return <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--text-3)' }}>暂无研报数据</div>
    }

    return (
        <div className="skd__reports">
            <div className="skd__reports-header">
                <span className="skd__reports-title">研究报告</span>
                <span className="skd__reports-count">{items.length} 篇</span>
                {usingMock && <span style={{ fontSize: 11, color: '#eab308', background: 'rgba(234,179,8,0.1)', padding: '1px 8px', borderRadius: 4 }}>演示数据</span>}
            </div>
            {items.map(r => {
                const isOpen = expanded.has(r.id)
                const anyR = r as any
                const typeLabel = reportTypeLabel[(r as any).report_type] ?? (r as any).report_type ?? '报告'
                return (
                    <div key={r.id} className="skd__report-row">
                        <div className="skd__report-main" onClick={() => toggle(r.id)}>
                            <div className="skd__report-meta">
                                <span className="skd__report-date">{r.published_date}</span>
                                <span className="skd__report-source">{anyR._source ?? typeLabel}</span>
                                <span className="skd__report-source" style={{ background: 'rgba(100,116,139,0.1)', color: '#64748b' }}>{typeLabel}</span>
                            </div>
                            <div className="skd__report-title">{r.title}</div>
                            {anyR._tags && (
                                <div className="skd__report-tags">
                                    {anyR._tags.map((t: string) => (
                                        <span key={t} className="skd__report-tag">{t}</span>
                                    ))}
                                </div>
                            )}
                            <span className="skd__report-toggle">{isOpen ? '收起 ▲' : '查看摘要 ▼'}</span>
                        </div>
                        {isOpen && (
                            <div className="skd__report-summary">
                                {anyR._summary
                                    ? anyR._summary
                                    : r.source_url
                                        ? <a href={r.source_url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>打开原始链接 →</a>
                                        : '暂无摘要内容'}
                            </div>
                        )}
                    </div>
                )
            })}
        </div>
    )
}
